import React, { useState } from "react";
import { Alert, Box, Button, Card, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { api } from "../../api/axios";
import { CheckList, PageHero, Section } from "./MarketingParts";

export default function MarketingContact() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSent(false);
    setLoading(true);
    try {
      await api.post("/api/public/contact", { name, email, phone, website, message });
      setSent(true);
      setName("");
      setEmail("");
      setPhone("");
      setWebsite("");
      setMessage("");
    } catch (err) {
      setError(err?.response?.data?.message || t("m_contact_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero eyebrow={t("mk_contact_eyebrow")} icon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} aria-hidden="true" />} title={t("m_contact_title")} subtitle={t("mk_contact_sub")} />

      <Section sx={{ pt: { xs: 5, md: 7 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.5fr 1fr" }, gap: { xs: 3, md: 4 }, alignItems: "start" }}>
          <Card sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h2" sx={{ fontSize: "1.25rem", mb: 3 }}>
              {t("mk_contact_form_t")}
            </Typography>
            <Box aria-live="polite">
              {sent ? <Alert severity="success" sx={{ mb: 2 }}>{t("m_contact_sent")}</Alert> : null}
              {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            </Box>

            <Box component="form" onSubmit={submit}>
              <Stack spacing={2}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <TextField label={t("m_contact_name")} value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
                  <TextField label={t("m_contact_email")} value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" inputProps={{ inputMode: "email", dir: "ltr" }} />
                  <TextField label={t("m_contact_phone")} value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" inputProps={{ inputMode: "tel", dir: "ltr" }} />
                  <TextField label={t("m_contact_website")} value={website} onChange={(e) => setWebsite(e.target.value)} autoComplete="url" inputProps={{ inputMode: "url", dir: "ltr" }} />
                </Box>
                <TextField
                  label={t("m_contact_message")}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  multiline
                  minRows={5}
                />
                <Box>
                  <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ minWidth: 180, width: { xs: "100%", sm: "auto" } }}>
                    {t("m_contact_send")}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Card>

          <Card sx={{ p: { xs: 3, md: 3.5 }, bgcolor: (th) => th.aw.softer, boxShadow: "none" }}>
            <Typography variant="h2" sx={{ fontSize: "1.1rem", mb: 2.5 }}>
              {t("mk_contact_side_t")}
            </Typography>
            <CheckList dense items={[t("mk_contact_side_1"), t("mk_contact_side_2"), t("mk_contact_side_3"), t("mk_contact_side_4")]} />
          </Card>
        </Box>
      </Section>
    </>
  );
}
